import { isDefined } from "@clipboard-health/util-ts";
import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { Text } from "../../components/Text";
import { ChatMessageTimestamp, getOutgoingReadStatus } from "../ChatMessageTimestamp";
import type { ReplyContext, TextChatMessage } from "../types";
import { formatMessageBubbleTime } from "../utils/formatMessageTime";
import { parseCustomMessage } from "../utils/parseCustomMessage";
import { MessageOverflowMenu } from "./MessageOverflowMenu";
import { ReplyIconButton } from "./ReplyIconButton";

interface ChatTextMessageProps {
  message: TextChatMessage;
  isIncoming: boolean;
  /**
   * IANA time zone of the workplace this chat belongs to (e.g. "America/New_York").
   * When provided, the per-message timestamp renders in this time zone so it
   * matches the channel list. When omitted, falls back to the viewer's device time zone.
   */
  facilityTimeZone?: string;
  onReply?: (context: ReplyContext) => void;
  onMarkAsUnread?: (messageId: number) => void;
  onEdit?: (message: TextChatMessage) => void;
  onDelete?: (message: TextChatMessage) => void;
  sx?: SxProps<Theme>;
}

function getMessageBubbleStyles(theme: Theme, isIncoming: boolean) {
  return {
    display: "inline-flex",
    flexDirection: "column" as const,
    gap: 1,
    paddingX: 4,
    paddingY: 3,
    borderRadius: theme.borderRadius?.xLarge,
    backgroundColor: isIncoming
      ? (theme.palette.background.chat?.incoming ?? theme.palette.background.muted)
      : (theme.palette.background.chat?.outgoing ?? theme.palette.primary.main),
    maxWidth: "80%",
    overflowWrap: "break-word" as const,
    wordBreak: "break-word" as const,
  };
}

/**
 * Renders a text message with optional reply context
 */
export function ChatTextMessage(props: ChatTextMessageProps) {
  const { message, isIncoming, facilityTimeZone, onReply, onMarkAsUnread, onEdit, onDelete, sx } =
    props;
  const displayMessage = parseCustomMessage(message.message);

  const markAsUnreadHandler =
    isIncoming && onMarkAsUnread
      ? () => {
          onMarkAsUnread(message.messageId);
        }
      : undefined;
  const editHandler =
    !isIncoming && onEdit
      ? () => {
          onEdit(message);
        }
      : undefined;
  const deleteHandler =
    !isIncoming && onDelete
      ? () => {
          onDelete(message);
        }
      : undefined;
  const showOverflowMenu = Boolean(markAsUnreadHandler ?? editHandler ?? deleteHandler);

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: isIncoming ? "flex-start" : "flex-end",
          gap: 1,
        },
        sx
      )}
    >
      <ChatMessageTimestamp
        senderName={message.senderName ?? "Unknown"}
        timestamp={formatMessageBubbleTime(message.createdAt, facilityTimeZone)}
        readStatus={getOutgoingReadStatus(isIncoming, message.readByOther)}
        isEdited={isDefined(message.updatedAt)}
      />
      <Box data-dd-privacy="mask" sx={(theme: Theme) => getMessageBubbleStyles(theme, isIncoming)}>
        {/* Show parent message if this is a reply */}
        {message.parentMessage && (
          <Box
            sx={(theme) => ({
              padding: 2,
              borderRadius: theme.borderRadius?.medium,
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              marginBottom: 1,
            })}
          >
            <Text
              semibold
              variant="caption"
              sx={(theme) => ({
                color: isIncoming ? theme.palette.text.secondary : theme.palette.common.white,
              })}
            >
              {message.parentMessage.senderName ?? "Unknown"}
            </Text>
            <Text
              variant="caption"
              sx={(theme) => ({
                color: isIncoming ? theme.palette.text.secondary : "rgba(255, 255, 255, 0.85)",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "250px",
              })}
            >
              {parseCustomMessage(message.parentMessage.message)}
            </Text>
          </Box>
        )}

        {/* Message text with inline reply button */}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: isIncoming ? theme.palette.base?.strong : theme.palette.base?.inverted?.strong,
              whiteSpace: "pre-wrap",
            })}
          >
            {displayMessage}
          </Text>
          {onReply && (
            <ReplyIconButton
              isIncoming={isIncoming}
              onClick={() => {
                onReply({
                  messageId: message.messageId,
                  message: displayMessage,
                  senderName: message.senderName ?? "Unknown",
                });
              }}
            />
          )}
          {showOverflowMenu && (
            <MessageOverflowMenu
              isIncoming={isIncoming}
              onMarkAsUnread={markAsUnreadHandler}
              onEdit={editHandler}
              onDelete={deleteHandler}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
